import { Component } from '@angular/core';
import { AuthService, StateService } from 'src/app/services';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Product2, CompanyType, ProductWorkflowState } from 'situview-webapi-client';
import { SituBaseComponent } from 'src/app/shared/situ-base-component';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ArtifactViewerDialogComponent } from 'src/app/components';

@Component({
  selector: 'product-catalog',
  templateUrl: './product-catalog.component.html',
  styleUrls: ['./product-catalog.component.scss']
})

export class ProductCatalogComponent extends SituBaseComponent {
  /* Search */
  searchTerm = "";
  productClassificationIdFilter: string | undefined;
  productTypeIdFilter: string | undefined;
  productStateFilter: ProductWorkflowState | undefined;
  companyIdFilter: string | undefined;
  brandIdFilter: string | undefined;
  productRangeIdFilter: string | undefined;
  productSpecificationSelectedIds: string[] = [];

  dialogRef: MatDialogRef<ArtifactViewerDialogComponent> | undefined;

  /* Query Parameters */
  readonly FilterParamName: string = "filter";
  readonly CompanyParamName: string = "company";
  readonly BrandParamName: string = "brand";
  readonly ProductClassificationParamName: string = "classification";
  readonly ProductTypeParamName: string = "type";
  readonly ProductStateParamName: string = "state";
  readonly ProductRangeParamName: string = "range";
  readonly SpecsParamName: string = "specs";

  public firstSelectedCompanyChange = true;

  constructor(
    authService: AuthService,
    stateService: StateService,
    private router: Router,
    private route: ActivatedRoute,
    private dialog: MatDialog) {
    super(authService, stateService);

    if (this.route.snapshot.queryParams[this.FilterParamName]) {
      this.searchTerm = this.route.snapshot.queryParams[this.FilterParamName];
    }

    if (this.route.snapshot.queryParams[this.CompanyParamName]) {
      this.companyIdFilter = this.route.snapshot.queryParams[this.CompanyParamName];
    }

    if (this.route.snapshot.queryParams[this.BrandParamName]) {
      this.brandIdFilter = this.route.snapshot.queryParams[this.BrandParamName];
    }

    if (this.route.snapshot.queryParams[this.ProductClassificationParamName]) {
      this.productClassificationIdFilter = this.route.snapshot.queryParams[this.ProductClassificationParamName];
    }

    if (this.route.snapshot.queryParams[this.ProductTypeParamName]) {
      this.productTypeIdFilter = this.route.snapshot.queryParams[this.ProductTypeParamName];
    }

    if (this.route.snapshot.queryParams[this.ProductStateParamName]) {
      this.productStateFilter = Number(this.route.snapshot.queryParams[this.ProductStateParamName]);
    }

    if (this.route.snapshot.queryParams[this.ProductRangeParamName]) {
      this.productRangeIdFilter = this.route.snapshot.queryParams[this.ProductRangeParamName];
    }

    if (this.route.snapshot.queryParams[this.SpecsParamName]) {
      this.productSpecificationSelectedIds = String(this.route.snapshot.queryParams[this.SpecsParamName])
        .split(',')
        .filter(id => !!id);
    }
  }

  ngOnInit(): void {
    super.ngOnInit();
  }

  protected onSelectedCompanyChange(): void {
    // When filtering the company we want to do the following:-
    // On First SelectedCompanyChange
    //   Filter if the company URL Param is set (done in the constructor)
    //     Otherwise
    //   Filter to the selected company if it is a Manufacturer
    // Else:
    //   Filter to the selected company

    if (this.firstSelectedCompanyChange) {
      this.firstSelectedCompanyChange = false;

      if (!this.companyIdFilter) {
        if (this.selectedCompany?.type === CompanyType.Manufacturer) {
          this.companyIdFilter = this.selectedCompany?.id;
          this.updateParams();
        }
      }
    } else {
      this.companyIdFilter = this.selectedCompany?.id;
      this.updateParams();
    }
  }

  onProductSelected(products: Product2[]) {
    var product = products[0];
    const screenWidth = window.innerWidth;
    const screenHeight = window.innerHeight;
  
    const dialogWidth = `${Math.round((70 / 100) * screenWidth)}px`;
    const dialogHeight = `${Math.round((90 / 100) * screenHeight)}px`;

    if (product?.previewArtifactId) {
      this.dialogRef = this.dialog.open(ArtifactViewerDialogComponent, {
        width: dialogWidth,
        height: dialogHeight,
        data: {
          fileName: "product.glb",
          displayName: product.name,
          artifactId: product.previewArtifactId,
          additionalFileNames: ArtifactViewerDialogComponent.productPreviewFiles,
        }
      });
    }
  }

  onFilterCompanyId(companyId: string) {
    this.companyIdFilter = companyId;
    this.updateParams();
  }

  onFilterBrandId(brandId: string) {
    this.brandIdFilter = brandId;
    this.updateParams();
  }

  onFilterProductClassificationId(productClassificationId: string) {
    this.productClassificationIdFilter = productClassificationId;
    this.updateParams();
  }

  onFilterProductTypeId(productTypeId: string) {
    this.productTypeIdFilter = productTypeId;
    this.updateParams();
  }

  onFilterProductState(productState: ProductWorkflowState) {
    this.productStateFilter = productState;
    this.updateParams();
  }

  onSearchTermChanged(searchTerm: string) {
    this.searchTerm = searchTerm;
    this.updateParams();
  }

  onFilterProductRange(rangeId: string) {
    this.productRangeIdFilter = rangeId;
    this.updateParams();
  }

  onFilterProductSpecificationSelectedIds(ids: string[]) {
    this.productSpecificationSelectedIds = ids ?? [];
    this.updateParams();
  }

  private updateParams() {
    const queryParams: { [key: string]: any } = {};

    if (this.searchTerm) {
      queryParams.filter = this.searchTerm;
    }
    if (this.companyIdFilter) {
      queryParams.company = this.companyIdFilter;
    }
    if (this.brandIdFilter) {
      queryParams.brand = this.brandIdFilter;
    }
    if (this.productClassificationIdFilter) {
      queryParams.classification = this.productClassificationIdFilter;
    }
    if (this.productTypeIdFilter) {
      queryParams.type = this.productTypeIdFilter;
    }
    if (this.productStateFilter) {
      queryParams.state = this.productStateFilter;
    }
    if (this.productRangeIdFilter) {
      queryParams.range = this.productRangeIdFilter;
    }
    if (this.productSpecificationSelectedIds?.length) {
      queryParams.specs = this.productSpecificationSelectedIds.join(',');
    }

    const urlTree = this.router.createUrlTree([], {
      queryParamsHandling: "merge",
      preserveFragment: true,
      queryParams: queryParams
    });

    if (!this.searchTerm) {
      delete urlTree.queryParams[this.FilterParamName];
    }
    if (!this.companyIdFilter) {
      delete urlTree.queryParams[this.CompanyParamName];
    }
    if (!this.brandIdFilter) {
      delete urlTree.queryParams[this.BrandParamName];
    }
    if (!this.productClassificationIdFilter) {
      delete urlTree.queryParams[this.ProductClassificationParamName];
    }
    if (!this.productTypeIdFilter) {
      delete urlTree.queryParams[this.ProductTypeParamName];
    }
    if (!this.productStateFilter) {
      delete urlTree.queryParams[this.ProductStateParamName];
    }
    if (!this.productRangeIdFilter) {
      delete urlTree.queryParams[this.ProductRangeParamName];
    }
    if (!this.productSpecificationSelectedIds?.length) {
      delete urlTree.queryParams[this.SpecsParamName];
    }

    this.router.navigateByUrl(urlTree);
  }
}
